import { useEffect, useState, useRef } from "react";
import { Loader2, Activity } from "lucide-react";
import { WebScrapeJob, WebScraperJobStatus } from "../../../services/web-scraper.service";

interface LiveProgressPanelProps {
  job: WebScrapeJob;
}

function formatDuration(ms: number) {
  if (ms < 1000) return "< 1s";
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return `${min}m ${sec}s`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hr}h ${remMin}m`;
}

export default function LiveProgressPanel({ job }: LiveProgressPanelProps) {
  // Tick the elapsed clock once per second so the user sees it move.
  const [, setNow] = useState(Date.now());
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (job.status !== WebScraperJobStatus.RUNNING) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [job.status]);

  // Only show this panel when the crawl is actively doing work AND a page cap
  // is configured — without max_pages there is no meaningful progress target.
  const hasPageCap = job.max_pages != null && job.max_pages > 0;
  if (job.status !== WebScraperJobStatus.RUNNING || !hasPageCap) return null;

  if (job.started_at) {
    startedAtRef.current = new Date(job.started_at).getTime();
  }

  const pagesCrawled = job.pages_crawled ?? 0;
  const maxPages = job.max_pages as number;
  const pct = Math.min(100, Math.round((pagesCrawled / maxPages) * 100));

  // ETA — pages per ms × pages remaining
  const elapsedMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
  const pagesPerMs = elapsedMs > 0 && pagesCrawled > 0 ? pagesCrawled / elapsedMs : 0;
  const remaining = Math.max(0, maxPages - pagesCrawled);
  const etaMs = pagesPerMs > 0 ? remaining / pagesPerMs : 0;
  const showEta = pagesPerMs > 0 && pagesCrawled >= 3;

  const shortUrl = (url: string | null | undefined) => {
    if (!url) return "—";
    if (url.length <= 90) return url;
    return `${url.slice(0, 90)}…`;
  };

  return (
    <div className="bg-white border border-blue-200 rounded-lg px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
          <Activity className="w-4 h-4 text-blue-600" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900">Crawl in progress</h3>
        <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">
            <span className="font-semibold text-gray-900">{pagesCrawled}</span> of <span className="font-semibold text-gray-900">{maxPages}</span> pages
          </span>
          <span className="font-medium text-blue-600">{pct}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Currently visiting */}
      <div className="text-xs space-y-1">
        <div className="flex items-start gap-2">
          <span className="text-gray-500 flex-shrink-0">Working on:</span>
          {job.current_url ? (
            <a
              href={job.current_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-700 break-all font-medium"
              title={job.current_url}
            >
              {shortUrl(job.current_url)}
            </a>
          ) : (
            <span className="text-gray-400 italic">starting up…</span>
          )}
        </div>

        <div className="flex items-center gap-4 text-gray-500 pt-1">
          <span>Elapsed: <span className="font-medium text-gray-700">{formatDuration(elapsedMs)}</span></span>
          {showEta && (
            <span>ETA: <span className="font-medium text-gray-700">~{formatDuration(etaMs)}</span></span>
          )}
        </div>
      </div>
    </div>
  );
}
